import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadMaps } from "../utils/loadMaps";
import "../styles/HealthyFoods.css";
import { apiUrl } from "../config";

export default function HealthyFoods() {
  const navigate = useNavigate();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);

  const [error, setError] = useState("");
  const [loadingMap, setLoadingMap] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [selected, setSelected] = useState(null);

  // picks state: { items, images, totalReviews }
  const [picks, setPicks] = useState({ items: [], images: [], totalReviews: 0 });
  const [loadingPicks, setLoadingPicks] = useState(false);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/result");
  };

  async function fetchImagesFor(term, provider = "all") {
    const url = apiUrl(`/api/images?q=${encodeURIComponent(term)}&perPage=12&provider=${provider}`);
    const r = await fetch(url);
    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      console.error("images api error:", r.status, txt.slice(0, 120));
      throw new Error("image api failed");
    }
    const j = await r.json();
    return Array.isArray(j.images) ? j.images : [];
  }

  const cacheRef = useRef(new Map());

  useEffect(() => {
    let alive = true;
    const init = async () => {
      try {
        setLoadingMap(true);
        setError("");

        const g = await loadMaps();
        if (!alive) return;

        const coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("no geo"));
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos.coords),
            (err) => reject(err),
            { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
          );
        });

        if (!alive) return;
        const center = { lat: coords.latitude, lng: coords.longitude };

        mapRef.current = new g.maps.Map(mapDivRef.current, {
          center,
          zoom: 14,
          mapId: process.env.REACT_APP_MAP_ID || undefined,
        });

        const service = new g.maps.places.PlacesService(mapRef.current);
        const request = {
          location: center,
          radius: 5000,
          query: "healthy restaurant vegan organic salad bowl quinoa",
        };

        service.textSearch(request, (results, status) => {
          if (!alive) return;
          if (status !== g.maps.places.PlacesServiceStatus.OK || !results?.length) {
            setRestaurants([]);
            setError("No healthy restaurants found nearby.");
            return;
          }

          const excludedChains = ["mcdonald", "jack in the box", "burger king", "wendy", "kfc", "taco bell", "popeyes", "chick-fil-a", "five guys", "sonic", "carl's jr", "little caesars", "domino", "pizza hut", "dairy queen", "in-n-out"];
          const healthySignals = ["healthy", "vegan", "vegetarian", "organic", "salad", "juice", "bowl", "mediterranean", "farm", "garden", "plant"];
          const items = results
            .filter((p) => {
              const n = (p.name || "").toLowerCase();
              const description = `${n} ${(p.types || []).join(" ")}`;
              const isExcluded = excludedChains.some((chain) => n.includes(chain)) || /burger|fried|pizza|donut|wings|hot dog/.test(n);
              return !isExcluded && healthySignals.some((signal) => description.includes(signal));
            })
            .slice(0, 12)
            .map((p) => {
              new g.maps.Marker({
                map: mapRef.current,
                position: p.geometry?.location,
                title: p.name,
              });

              return {
                placeId: p.place_id,
                name: p.name,
                address: p.formatted_address || p.vicinity || "",
                rating: p.rating || "N/A",
                mapsUrl: `https://www.google.com/maps/place/?q=place_id:${p.place_id}`,
              };
            });

          setRestaurants(items);

          if (items.length) {
            setSelected(items[0]);
            void fetchPicks(items[0].placeId, items[0].name);
          }
        });
      } catch (e) {
        if (alive) setError("Failed to load Google Maps.");
      } finally {
        if (alive) setLoadingMap(false);
      }
    };

    init();
    return () => { alive = false; };
    // Map setup only runs once; selections invoke fetchPicks directly afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPicks = async (placeId, placeName = "") => {
    if (!placeId) return;

    if (cacheRef.current.has(placeId)) {
      setPicks(cacheRef.current.get(placeId));
      return;
    }

    setLoadingPicks(true);
    setError("");

    try {
      const term = placeName ? `${placeName} healthy dishes` : "healthy food dishes";
      const images = await fetchImagesFor(term, "all");
      const payload = { items: [], images: Array.isArray(images) ? images : [], totalReviews: 0 };
      cacheRef.current.set(placeId, payload);
      setPicks(payload);
    } catch (e) {
      console.error("fetchPicks failed:", e);
      setPicks({ items: [], images: [], totalReviews: 0 });
    } finally {
      setLoadingPicks(false);
    }
  };

  return (
    <div className="map-container">
      <div className="food-header">
        <h3>Healthy Restaurants Near You</h3>
        <button className="back-btn" onClick={goBack}>⬅ Back</button>
      </div>

      {error && <p className="error">{error}</p>}

      <div
        ref={mapDivRef}
        style={{ width: "100%", height: 450, marginBottom: 20 }}
        aria-busy={loadingMap ? "true" : "false"}
      />

      <div className="hf-split">
        <div className="food-list">
          <h3>Healthy Restaurants Near You</h3>
          <ul>
            {restaurants.length ? (
              restaurants.map((rest) => (
                <li
                  key={rest.placeId}
                  className={selected?.placeId === rest.placeId ? "hf-active" : ""}
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => { setSelected(rest); fetchPicks(rest.placeId, rest.name); }}
                  onFocus={() => { setSelected(rest); fetchPicks(rest.placeId, rest.name); }}
                  onTouchStart={() => { setSelected(rest); fetchPicks(rest.placeId, rest.name); }}
                >
                  <a
                    href={rest.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="restaurant-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {rest.name}
                  </a>
                  <br />
                  <small>{rest.address}</small>
                  <br />
                  ⭐ {rest.rating}
                </li>
              ))
            ) : (
              <li>{loadingMap ? "Loading map..." : "No healthy restaurants found nearby."}</li>
            )}
          </ul>
        </div>

        <div className="hf-picks">
          <h3>{selected ? `Popular healthy picks — ${selected.name}` : "Popular healthy picks"}</h3>

          <div className="hf-image-box" style={{ marginBottom: 12 }}>
            {loadingPicks ? (
              <div className="hf-image-empty">Loading images…</div>
            ) : picks.images?.length ? (
              <>
                <div className="hf-image-grid" style={{ marginBottom: 8 }}>
                  {picks.images.map((img, i) => (
                    <img
                      key={i}
                      src={img.src}
                      alt={img.alt || "dish"}
                      loading="lazy"
                      style={{ borderRadius: 6 }}
                    />
                  ))}
                </div>
                <small style={{ fontSize: "0.75rem", opacity: 0.6, display: "block" }}>
                  Images shown are for reference only
                </small>
              </>
            ) : (
              <div className="hf-image-empty">No image yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
